import { z } from 'zod';

/**
 * De openbare rapportpagina zoals hij over de lijn komt:
 * `GET /public/reports/{token}` (`Public\PublicReportResource` in `smartkeur-api`).
 *
 * De API stuurt een allow-list, veld voor veld. Zod laat onbekende sleutels
 * vallen, dus een veld dat hier niet staat (marge, vergoeding, interne notitie,
 * een ander dossier) haalt het scherm nooit - voeg hier dus nooit een veld toe
 * dat niet op de pagina hoort. `model.ts` maakt er het model van.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** Bedrag als string met twee decimalen: `"1450.00"`. */
const amount = z.string();

export const publicTenantResourceSchema = z.object({
    name: z.string(),
    logo_url: z.string().nullable(),
    accent_color: z.string().nullable(),
    /* De twee kleuren naast het accent kwamen later; een oud antwoord heeft ze niet. */
    accent_text_color: z.string().nullable().optional(),
    heading_color: z.string().nullable().optional(),
    phone: z.string().nullable(),
    /*
     * De rest van de bedrijfsgegevens kwam later; een oud antwoord heeft ze niet. Het
     * adres zoals het rapport het drukt, regel voor regel en zonder lege regel; de
     * website altijd als http(s)-adres.
     */
    email: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    address_lines: z.array(z.string()).optional(),
    opening_hours: z.string().nullable(),
    kvk: z.string().nullable(),
    memberships: z.array(z.string()),
    languages: z.array(z.string()),
});

export type PublicTenantResource = z.infer<typeof publicTenantResourceSchema>;

/**
 * Een rapporttekst zoals de API hem stuurt: de geparste markup als boom, nooit
 * html (`ReportTextRenderer::tree()`). Een blok dat deze app niet kent, valt weg
 * in plaats van de pagina te breken.
 */
const reportInlineSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string() }),
    z.object({ type: z.literal('link'), text: z.string(), href: z.string() }),
]);

const reportBlockSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('paragraph'), lines: z.array(z.array(reportInlineSchema)) }),
    z.object({ type: z.literal('list'), items: z.array(z.array(reportInlineSchema)) }),
]);

export const reportTextSchema = z.array(z.unknown()).transform((blocks) =>
    blocks.flatMap((block) => {
        const parsed = reportBlockSchema.safeParse(block);

        return parsed.success ? [parsed.data] : [];
    }),
);

export type ReportTextResource = z.infer<typeof reportTextSchema>;

const costAmountsSchema = z.object({ direct: amount, termijn: amount, onderhoud: amount });

const fullReportEntrySchema = z.object({
    id: z.string(),
    /** Niet in de PDF: voor de lijst achter een kostenbedrag. */
    title: z.string().catch(''),
    text: reportTextSchema,
    location: z.string().nullable(),
    /** De ernst van de bevinding zelf: `gebrek`, `aandacht`, `goed` of `null`. */
    severity: z.string().nullable().catch(null),
    /** Een opmerking staat erbij zoals in de PDF, maar telt nooit als bevinding. */
    remark: z.boolean().catch(false),
    /** Alleen de urgenties met een bedrag; leeg als de link de kosten verbergt. */
    costs: z.array(z.object({ key: z.string(), amount })),
    nhg_code: z.string().nullable(),
    photos: z.array(
        z.object({ thumb_url: z.string(), url: z.string(), caption: z.string().nullable() }),
    ),
});

const chapterBase = {
    key: z.string(),
    number: z.string().nullable(),
    title: z.string(),
    /**
     * Hoort in de bijlage "Toelichting en voorwaarden", achteraan: de API beslist
     * het met dezelfde regel als de PDF. Een ouder antwoord heeft het veld niet.
     */
    appendix: z.boolean().optional(),
};

/**
 * De kleur van een oordeel: `green` · `yellow` · `orange` · `red` · `blue`, of
 * `null` zonder oordeel. Een ouder antwoord heeft het veld niet.
 */
const tone = z.string().nullable().optional();

/** Een regel van "Zo leest u dit rapport": het label en wat het betekent. */
const guideItemSchema = z.object({ key: z.string(), label: z.string(), text: z.string() });

const nhgLineSchema = z.object({
    label: z.string(),
    direct: amount,
    termijn: amount,
    total: amount,
});

const labelledAmountSchema = z.object({ label: z.string(), amount });

const titledListSchema = z.object({ title: z.string(), items: z.array(z.string()) });

const fullReportChapterSchema = z.discriminatedUnion('kind', [
    z.object({ ...chapterBase, kind: z.literal('summary'), text: reportTextSchema }),
    z.object({
        ...chapterBase,
        kind: z.literal('guide'),
        /** `null` als de link de kosten verbergt: dan zijn er geen kostenblokken uit te leggen. */
        costs: z
            .object({ title: z.string(), items: z.array(guideItemSchema) })
            .nullable()
            .catch(null),
        judgements: z.object({
            title: z.string(),
            note: z.string().nullable().catch(null),
            items: z.array(guideItemSchema.extend({ tone: z.string() })),
        }),
    }),
    z.object({
        ...chapterBase,
        kind: z.literal('costs'),
        text: reportTextSchema,
        parts: z.array(costAmountsSchema.extend({ label: z.string() })),
        total: costAmountsSchema,
        groups: z.array(costAmountsSchema.extend({ name: z.string() })),
    }),
    z.object({
        ...chapterBase,
        kind: z.literal('part'),
        judgement: z.string().nullable(),
        tone,
        severity: z.string().nullable(),
        /** Wat het onderdeel kost, zoals in het hoofdstuk Kosten; `null` als de link de kosten verbergt. */
        costs: costAmountsSchema.nullable().catch(null),
        note: reportTextSchema,
        entries: z.array(fullReportEntrySchema),
        headings: z.array(
            z.object({
                /** Het anker van een diepe link (`6-2`); ontbreekt het, dan maakt de app er een. */
                key: z.string().optional(),
                number: z.string().nullable(),
                title: z.string(),
                /** De echte diepte: 2, 3, … */
                level: z.number(),
                judgement: z.string().nullable(),
                tone,
                severity: z.string().nullable(),
                entries: z.array(fullReportEntrySchema),
            }),
        ),
    }),
    z.object({
        ...chapterBase,
        kind: z.literal('nhg'),
        model: z.string().nullable(),
        intro: z.string(),
        summary: z.object({
            title: z.string(),
            columns: z.array(z.string()),
            rows: z.array(nhgLineSchema),
            total: nhgLineSchema,
            improvement: labelledAmountSchema,
        }),
        reconciliation: z.object({
            title: z.string(),
            rows: z.array(labelledAmountSchema),
            note: z.string(),
        }),
        remarks: titledListSchema,
        results: z.array(
            z.object({
                title: z.string(),
                note: z.string().nullable(),
                columns: z.array(z.string()),
                rows: z.array(
                    z.object({
                        code: z.string(),
                        element: z.string(),
                        locations: z.array(z.string()),
                        action: z.string(),
                        share: z.string(),
                        direct: amount,
                        termijn: amount,
                    }),
                ),
                total: z.object({ label: z.string(), direct: amount, termijn: amount }),
            }),
        ),
        legend: titledListSchema,
        programme: z.object({
            title: z.string(),
            columns: z.array(z.string()),
            rows: z.array(
                z.object({
                    code: z.string(),
                    title: z.string(),
                    items: z.array(z.string()),
                    bbl: z.array(z.string()),
                }),
            ),
        }),
    }),
    z.object({
        ...chapterBase,
        kind: z.literal('conditions'),
        text: reportTextSchema,
        footer_text: z.string().nullable(),
        company: z.string(),
    }),
]);

export type FullReportChapterResource = z.infer<typeof fullReportChapterSchema>;

/**
 * Het volledige geschreven rapport, hoofdstuk voor hoofdstuk zoals in de PDF
 * (`Public\PublicFullReportResource`). Een hoofdstuk van een soort die deze app
 * nog niet kent, valt weg.
 */
export const fullReportResourceSchema = z.object({
    cover: z.object({
        kind: z.string(),
        norm: z.string().nullable(),
        street: z.string(),
        place: z.string(),
        inspected_at: z.string().nullable(),
        inspector: z.string().nullable(),
        order_number: z.string(),
        version: z.number(),
        generated_at: timestamp.nullable(),
    }),
    chapters: z.array(z.unknown()).transform((chapters) =>
        chapters.flatMap((chapter) => {
            const parsed = fullReportChapterSchema.safeParse(chapter);

            return parsed.success ? [parsed.data] : [];
        }),
    ),
});

export type FullReportResource = z.infer<typeof fullReportResourceSchema>;

/** `GET /public/reports/{token}/translation?lang=` en `translation` op de pagina. */
export const publicTranslationResourceSchema = z.object({
    language: z.string(),
    /** `pending` · `ready` · `failed` */
    status: z.string(),
});

export type PublicTranslationResource = z.infer<typeof publicTranslationResourceSchema>;

export const publicReportResourceSchema = z.object({
    order_number: z.string(),
    language: z.string(),
    available_languages: z.array(z.string()),
    /** De taal waar de koper om vroeg en hoe ver de vertaling is; `null` voor Nederlands. */
    translation: publicTranslationResourceSchema.nullable().optional(),
    tenant: publicTenantResourceSchema,
    published_at: timestamp.nullable(),
    valid_until: timestamp,
    service: z.string(),
    address: z.string(),
    /** Het leesbare deel van de link, `saffier-4-hoorn` in `/r/{token}/saffier-4-hoorn`. */
    address_slug: z.string().default(''),
    summary: z.string(),
    documents: z.array(
        z.object({
            key: z.string(),
            label: z.string(),
            url: z.string(),
            detail: z.string().nullable(),
        }),
    ),
    costs: z.object({
        visible: z.boolean(),
        blocks: z.array(
            z.object({
                /** `direct` · `termijn` · `onderhoud` */
                key: z.string(),
                amount,
                count: z.number().nullable(),
            }),
        ),
        total: amount.nullable(),
    }),
    /**
     * De NHG-bijlage: alleen de regels met kosten, en `null` zonder bijlage of als
     * de link de kosten verbergt.
     */
    nhg: z
        .object({
            tables: z.array(
                z.object({
                    /** `general` · `storeys` */
                    key: z.string(),
                    label: z.string(),
                    rows: z.array(
                        z.object({
                            code: z.string(),
                            element: z.string(),
                            locations: z.array(z.string()),
                            direct: amount,
                            termijn: amount,
                        }),
                    ),
                    total: z.object({ direct: amount, termijn: amount }),
                }),
            ),
            total: z.object({ direct: amount, termijn: amount }),
            improvement: amount,
            outside: amount,
        })
        .nullable()
        .default(null),
    findings: z.array(
        z.object({
            id: z.string(),
            title: z.string(),
            urgency: z.string(),
            explanation: z.string(),
            /** Waar het gebrek zit, in de woorden van de inspecteur; leeg als er niets staat. */
            location: z.string().nullable().default(null),
            section: z.string(),
            amount: amount.nullable(),
            photo_url: z.string().nullable(),
            photo_caption: z.string().nullable(),
        }),
    ),
    parts: z.array(
        z.object({
            number: z.string(),
            label: z.string(),
            detail: z.string().nullable(),
            /** Het oordeel in woorden ("Matig") en zijn kleur; `null` zonder oordeel. */
            judgement: z.string().nullable().optional(),
            tone,
            /** `goed` · `aandacht` · `gebrek` */
            severity: z.string().nullable(),
        }),
    ),
    parts_note: z.string().nullable(),
    energy_label: z
        .object({
            code: z.string(),
            usage: z.number().nullable(),
            advice: z.string().nullable(),
            pdf_url: z.string().nullable(),
        })
        .nullable(),
    inspector: z
        .object({
            name: z.string(),
            qualification: z.string().nullable(),
            response_time: z.string().nullable(),
            phone: z.string().nullable(),
            avatar_url: z.string().nullable(),
        })
        .nullable(),
    sharing: z.object({
        enabled: z.boolean(),
        recipients: z.array(
            z.object({
                /** Gemaskeerd: "ad…@hypotheekadvies.example". */
                name: z.string(),
                shared_at: timestamp.nullable(),
            }),
        ),
    }),
    invoice: z
        .object({
            number: z.string(),
            amount,
            /** `open` · `paid` · `overdue` */
            status: z.string(),
            payment_note: z.string().nullable(),
            pdf_url: z.string().nullable(),
        })
        .nullable(),
    footer: z.object({
        disclaimer: z.string(),
        company: z.string().nullable(),
        kvk: z.string().nullable(),
        memberships: z.array(z.string()),
    }),
    /**
     * Het volledige rapport onder het tabblad Rapport. Kan het niet gelezen
     * worden, dan blijft het overzicht gewoon staan, zonder dat tabblad.
     */
    full_report: fullReportResourceSchema.nullable().catch(null),
});

export type PublicReportResource = z.infer<typeof publicReportResourceSchema>;

/**
 * De briefkop van het keuringsbedrijf in een foutantwoord: dezelfde waarden als
 * in `tenant` van de pagina, maar alleen wat de foutpagina tekent.
 */
export const publicLetterheadResourceSchema = z.object({
    name: z.string(),
    phone: z.string().nullable(),
    logo_url: z.string().nullable(),
    accent_color: z.string().nullable(),
    accent_text_color: z.string().nullable().optional(),
    heading_color: z.string().nullable().optional(),
});

export type PublicLetterheadResource = z.infer<typeof publicLetterheadResourceSchema>;

/**
 * Wat een foutantwoord van de publieke endpoints extra meestuurt:
 * `expired_at` bij **410**, `attempts_left` bij **422** `invalid-access-code`,
 * en - als het token bij een echte link hoort - `tenant` bij **410**,
 * `link-revoked`, `access-code-required` en `invalid-access-code`.
 */
export const publicReportProblemExtensionsSchema = z.object({
    expired_at: timestamp.nullable().catch(null).default(null),
    attempts_left: z.number().nullable().catch(null).default(null),
    tenant: publicLetterheadResourceSchema.nullable().catch(null).default(null),
});
